import { useQuery } from '@tanstack/react-query'
import { useOrganization } from '@/contexts/OrganizationContext'
import { createTenantDb, type FacilitiesPageQuery } from '@/lib/db-tenant'
import { toFacility } from '@/lib/facility-row'
import { BANKING_KEYS, keepPreviousForOrg } from '@/lib/query-keys'

/**
 * One server-side page of the Settings → Facilities table — active and
 * inactive facilities, filtered by name, status and pack — with the filter
 * set's total. Other screens keep using `useFacilities` (the whole list).
 *
 * The previous page stays on screen while the next one loads (same org only),
 * so the table and its footer do not collapse between pages.
 */
export function useFacilitiesPage(query: FacilitiesPageQuery) {
  const orgId = useOrganization().currentOrganization?.id
  return useQuery({
    queryKey: BANKING_KEYS.facilitiesPage(orgId, query),
    queryFn: async () => {
      if (!orgId) throw new Error('No organization selected')
      const { data, error } = await createTenantDb(orgId).facilities.listPage(query)
      if (error) throw new Error(error.message)
      return { items: data.items.map(toFacility), total: data.total }
    },
    enabled: !!orgId,
    placeholderData: keepPreviousForOrg(orgId, 2),
  })
}

/**
 * Live bank accounts per facility for the given facilities (the rows of one
 * table page): every non-deleted account, manual or connected, whatever the
 * facility's status. Every requested id is present in `data`, 0 when it has
 * none; `data` is undefined until the counts are known.
 */
export function useFacilityBankAccountCounts(facilityIds: string[]) {
  const orgId = useOrganization().currentOrganization?.id
  return useQuery({
    queryKey: BANKING_KEYS.facilityBankAccountCounts(orgId, facilityIds),
    queryFn: async () => {
      if (!orgId) throw new Error('No organization selected')
      const { data, error } = await createTenantDb(orgId).bankAccounts.countByFacility(facilityIds)
      if (error) throw new Error(error.message)
      return data
    },
    enabled: !!orgId && facilityIds.length > 0,
  })
}
